import React, { useEffect, useRef, useState } from "react";
import Map, { Marker, type MapRef } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import bbox from "@turf/bbox";
import {
  TerraDraw,
  TerraDrawPointMode,
  TerraDrawLineStringMode,
  TerraDrawPolygonMode,
  TerraDrawRectangleMode,
  TerraDrawRenderMode,
  TerraDrawModeUndoRedo,
  TerraDrawUndoRedoKeyboardShortcuts,
  type GeoJSONStoreFeatures,
  type HexColor
} from "terra-draw";
import { TerraDrawMapLibreGLAdapter } from "terra-draw-maplibre-gl-adapter";
import DrawingTools from "./DrawingTools";
import MapControls from "./map-controls/MapControls";
import { useGeolocation } from "../../hooks/useGeolocation";
import { CmTableSpatialTypeRaw, normaliseSpatialType } from "../../services/cmTables";
import { DEFAULT_MAP_VIEW } from "../../utilities/mapView";
import { APP_BASEMAP_STYLE } from "../../utilities/basemap";

export type DrawMode = "point" | "polygon" | "rectangle" | "line" | null;

interface CmTableDrawMapProps {
  spatialType: CmTableSpatialTypeRaw;
  /** Multi-geometry forms: the set of drawable kinds (point/linestring/polygon). Shows a tool per kind. */
  allowedTypes?: Array<"point" | "linestring" | "polygon">;
  initialViewState?: { longitude: number; latitude: number; zoom: number };
  externalGeometry?: GeoJSON.Geometry | GeoJSON.Feature | GeoJSON.FeatureCollection | null;
  onGeometryChange: (geometry: GeoJSON.Geometry | null) => void;
  /** Preview color (polygon/line) resolved from the selected styling `type` — recolors the drawn feature live. */
  previewColor?: string;
  /** Preview icon URL (point) resolved from the selected styling `type` — shown as a marker on the drawn point. */
  previewIcon?: string;
}

const EMPTY_VIEW_STATE = DEFAULT_MAP_VIEW;

// Idle/display mode — no drawing interaction, just renders the confirmed feature.
const RENDER_MODE = "render";

// Maps our toolbar's DrawMode to Terra Draw's registered mode names.
const TERRA_DRAW_MODE: Record<Exclude<DrawMode, null>, string> = {
  point: "point",
  polygon: "polygon",
  rectangle: "rectangle",
  line: "linestring"
};

/**
 * Terra Draw operates on single-component geometries (Point, LineString, Polygon).
 * For Multi* geometries we expand into individual components so all rings/lines/points
 * are visible in the preview map.  On save the drawn single geometry replaces the
 * previous multi-geometry — acceptable for the single-component draw workflow (MP-2).
 * Full multi-component vertex editing is tracked in CM-FE160-24.
 */
function expandToComponents(geometry: GeoJSON.Geometry | any): Array<{ geometry: GeoJSON.Geometry; modeName: string }> {
  // Handle FeatureCollection — expand each feature's geometry
  if ((geometry as any).type === "FeatureCollection") {
    return ((geometry as any).features ?? []).flatMap((f: any) => (f?.geometry ? expandToComponents(f.geometry) : []));
  }
  // Handle Feature — unwrap the geometry
  if ((geometry as any).type === "Feature") {
    return (geometry as any).geometry ? expandToComponents((geometry as any).geometry) : [];
  }
  switch (geometry.type) {
    case "Point":
      return [{ geometry, modeName: "point" }];
    case "LineString":
      return [{ geometry, modeName: "linestring" }];
    case "Polygon":
      return [{ geometry, modeName: "polygon" }];
    case "MultiPoint":
      return (geometry as GeoJSON.MultiPoint).coordinates.map((c) => ({
        geometry: { type: "Point" as const, coordinates: c },
        modeName: "point"
      }));
    case "MultiLineString":
      return (geometry as GeoJSON.MultiLineString).coordinates.map((c) => ({
        geometry: { type: "LineString" as const, coordinates: c },
        modeName: "linestring"
      }));
    case "MultiPolygon":
      return (geometry as GeoJSON.MultiPolygon).coordinates.map((c) => ({
        geometry: { type: "Polygon" as const, coordinates: c },
        modeName: "polygon"
      }));
    default:
      return [];
  }
}

// Terra Draw silently rejects two kinds of coordinate, and both leave the map blank:
//  - more than `coordinatePrecision` (default 9) decimal places — "excessive precision";
//  - a position that isn't exactly two numbers — "invalid coordinates". Terra Draw's
//    `coordinateIsValid` tests `coordinate.length === 2`, so a 3D [lng, lat, z] position
//    fails even though it is valid GeoJSON.
// Imported GPS tracks hit both: every position carries an elevation, often alongside 14+
// decimals. So every position is trimmed to [lng, lat] and rounded to 9 dp (~0.1mm —
// visually lossless) before it reaches Terra Draw. Z is dropped for display only;
// EntityForm strips it again on save for PostGIS.
const TERRA_DRAW_MAX_PRECISION = 9;

export const sanitiseCoordinates = (coords: any): any => {
  if (!Array.isArray(coords)) return coords;
  // A position — [lng, lat] or [lng, lat, z]. Drop Z, round the pair.
  if (typeof coords[0] === "number") {
    return coords.slice(0, 2).map((n: any) => (typeof n === "number" ? Number(n.toFixed(TERRA_DRAW_MAX_PRECISION)) : n));
  }
  // A nested ring / line / polygon array — recurse.
  return coords.map(sanitiseCoordinates);
};

const toFeature = (geometry: GeoJSON.Geometry, modeName: string): GeoJSONStoreFeatures => {
  const sanitised =
    "coordinates" in geometry ? { ...geometry, coordinates: sanitiseCoordinates((geometry as any).coordinates) } : geometry;
  return {
    type: "Feature",
    geometry: sanitised,
    properties: { mode: modeName }
  } as unknown as GeoJSONStoreFeatures;
};

/**
 * Terra Draw draws one draggable dot per vertex when `showCoordinatePoints` is on, which
 * is right for a hand-drawn shape but wrong for an imported GPS track: a 2,683-point
 * track becomes 2,684 store features, burying the line under a solid band of its own
 * handles and making every hover hit-test walk the whole track. Past this many vertices
 * the geometry renders as a plain line/outline with no per-vertex handles.
 */
export const MAX_EDITABLE_VERTICES = 500;

const countPositions = (coords: any): number => {
  if (!Array.isArray(coords)) return 0;
  if (typeof coords[0] === "number") return 1;
  return coords.reduce((total: number, c: any) => total + countPositions(c), 0);
};

/**
 * Total vertices in any GeoJSON value — geometry, Feature or FeatureCollection.
 *
 * Exported so a caller can decide *before* mounting this map whether hand-editing
 * is realistic. Past {@link MAX_EDITABLE_VERTICES} the canvas drops its per-vertex
 * handles and the shape can only be viewed, which is worth saying up front rather
 * than presenting a draw surface that appears broken.
 */
export const countGeometryVertices = (geojson: any): number =>
  expandToComponents(geojson ?? {}).reduce((total, { geometry }) => total + countPositions((geometry as any).coordinates), 0);

/**
 * Decides per-vertex handles for the geometry about to be seeded. Applied *before*
 * the components are added so dense tracks never create the points at all, rather
 * than creating and immediately deleting thousands of them.
 */
const applyVertexHandles = (draw: TerraDraw, components: Array<{ geometry: GeoJSON.Geometry }>) => {
  const vertices = components.reduce((total, { geometry }) => total + countPositions((geometry as any).coordinates), 0);
  const showCoordinatePoints = vertices <= MAX_EDITABLE_VERTICES;
  if (!showCoordinatePoints) {
    console.info(`[CmTableDrawMap] ${vertices} vertices exceeds ${MAX_EDITABLE_VERTICES} — rendering without per-vertex handles.`);
  }
  for (const mode of ["linestring", "polygon"]) {
    try {
      (draw as any).updateModeOptions(mode, { showCoordinatePoints });
    } catch (e) {
      console.warn(`[CmTableDrawMap] could not toggle vertex handles for ${mode}:`, e);
    }
  }
};

/**
 * Adds one expanded component to Terra Draw, reporting why it was refused.
 * `addFeatures` returns per-feature validation results instead of throwing, so a
 * rejected component otherwise disappears from the map with nothing in the console.
 * Returns whether the component actually made it onto the canvas.
 */
const addComponent = (draw: TerraDraw, geometry: GeoJSON.Geometry, modeName: string, phase: string): boolean => {
  try {
    const [result] = draw.addFeatures([toFeature(geometry, modeName)]);
    if (result && result.valid === false) {
      console.warn(`[CmTableDrawMap] Terra Draw rejected a ${geometry.type} during ${phase}:`, result.reason);
      return false;
    }
    return true;
  } catch (e) {
    console.warn(`[CmTableDrawMap] addFeatures threw for a component during ${phase}:`, e);
    return false;
  }
};

/** The base kind (point/linestring/polygon) of a geometry — drives toolbar locking + row spatial_type. */
export function kindOfGeometry(geom: GeoJSON.Geometry | null | undefined): "point" | "linestring" | "polygon" | null {
  const t = geom?.type;
  if (t === "Point" || t === "MultiPoint") return "point";
  if (t === "LineString" || t === "MultiLineString") return "linestring";
  if (t === "Polygon" || t === "MultiPolygon") return "polygon";
  return null;
}

/**
 * Whether the spatial type holds multiple components of one base kind.
 * True for the explicit `multi*` variants AND the `geometry` catch-all — a
 * `geometry` column accepts any single base kind but allows multiple features
 * of that kind (combined into a Multi* geometry), locked to the first kind drawn.
 */
function isMultiSpatialType(spatialType: string): boolean {
  const s = spatialType.toLowerCase();
  return s.startsWith("multi") || s === "geometry";
}

/**
 * The always-on editable Terra Draw mode for a spatial type. For fixed single/multi
 * kinds this is the base kind's mode. For the `geometry` catch-all (base "none") we
 * fall back to the kind already drawn so its vertices stay editable; with nothing
 * drawn yet there's no fixed kind, so we idle in RENDER_MODE until a tool is picked.
 */
function primaryModeFor(spatialType: string, drawnKind: "point" | "linestring" | "polygon" | null): string {
  const b = normaliseSpatialType(spatialType);
  const kind = b !== "none" ? b : drawnKind;
  return kind === "point" ? "point" : kind === "linestring" ? "linestring" : kind === "polygon" ? "polygon" : RENDER_MODE;
}

const CmTableDrawMap: React.FC<CmTableDrawMapProps> = ({
  spatialType,
  allowedTypes,
  initialViewState,
  externalGeometry,
  onGeometryChange,
  previewColor,
  previewIcon
}) => {
  const theme = useTheme();
  // Draw-colour resolution mirrors the main map's fallback chain: `previewColor`
  // already encodes row-level → table-level styling; when neither is set we fall
  // back to the theme's primary colour (MUI theme = config.colorScheme.primary).
  const baseDrawColor = (previewColor ?? theme.palette.primary.main) as HexColor;
  const mapRef = useRef<MapRef | null>(null);
  const drawRef = useRef<TerraDraw | null>(null);

  const [activeMode, setActiveMode] = useState<DrawMode>(null);
  const [hasGeometry, setHasGeometry] = useState(false);
  // The kind of the currently-drawn geometry (point/linestring/polygon) — locks the
  // toolbar to that kind while a geometry exists (multi-geometry maps).
  const [drawnKind, setDrawnKind] = useState<"point" | "linestring" | "polygon" | null>(null);
  const [mapReady, setMapReady] = useState(false);
  // Coordinates of the drawn point (point tables only) — anchors the preview icon marker.
  const [pointCoords, setPointCoords] = useState<[number, number] | null>(null);

  // Prevents feedback loop: when geometry comes from a draw/edit event we set this
  // flag so the externalGeometry sync useEffect skips that render cycle.
  const skipSyncRef = useRef(false);

  const { userLocation, isTrackingLocation, isGettingLocation, handleLocationRequest } = useGeolocation(mapRef);

  const handleZoomIn = () => {
    const map = mapRef.current?.getMap();
    if (map) map.easeTo({ zoom: map.getZoom() + 1 });
  };

  const handleZoomOut = () => {
    const map = mapRef.current?.getMap();
    if (map) map.easeTo({ zoom: map.getZoom() - 1 });
  };

  // Auto-zoom to the initial geometry (edit mode) — fire only once
  const autoZoomedRef = useRef(false);

  // Keep the latest callback without re-creating the Terra Draw instance
  const onGeometryChangeRef = useRef(onGeometryChange);
  onGeometryChangeRef.current = onGeometryChange;

  // Terra Draw manages the canvas cursor per-mode (crosshair to draw, grab to drag a
  // vertex); this is only the fallback for the surrounding container.
  const cursorStyle = activeMode ? "crosshair" : "default";

  // ── Initialise Terra Draw once the map is ready ────────────────────────────
  useEffect(() => {
    if (!mapReady || drawRef.current) return;
    const map = mapRef.current?.getMap();
    if (!map) return;

    const baseSpatial = normaliseSpatialType(spatialType);
    const isMultiType = isMultiSpatialType(spatialType);

    // A feature is only emittable once it forms a valid geometry. While the user is
    // mid-draw (e.g. a line with a single click) the feature is incomplete and skipped.
    const isValidFeature = (f: any): boolean => {
      const g = f.geometry;
      if (g.type === "Point") return Array.isArray(g.coordinates) && g.coordinates.length >= 2;
      if (g.type === "LineString") return Array.isArray(g.coordinates) && g.coordinates.length >= 2;
      if (g.type === "Polygon") return Array.isArray(g.coordinates?.[0]) && g.coordinates[0].length >= 4;
      return true;
    };

    // Collect all complete drawn features and emit a single combined geometry.
    // Display-only RENDER_MODE features are excluded, as are terra-draw's internal
    // helper features (coordinatePoint vertex dots, closingPoint, snappingPoint) —
    // they share the same mode name as the real geometry so mode alone won't filter them.
    const isInternalMarker = (f: any) =>
      f.properties.coordinatePoint === true || f.properties.closingPoint === true || f.properties.snappingPoint === true;

    const emitCombined = (draw: TerraDraw) => {
      const drawn = draw.getSnapshot().filter((f: any) => f.properties.mode !== RENDER_MODE && !isInternalMarker(f) && isValidFeature(f));
      if (drawn.length === 0) {
        onGeometryChangeRef.current(null);
        setHasGeometry(false);
        setDrawnKind(null);
        return;
      }
      setHasGeometry(true);
      setDrawnKind(kindOfGeometry(drawn[drawn.length - 1].geometry as GeoJSON.Geometry));

      const lineStrings = drawn.filter((f: any) => f.geometry.type === "LineString");
      const polygons = drawn.filter((f: any) => f.geometry.type === "Polygon");
      const points = drawn.filter((f: any) => f.geometry.type === "Point");

      // Single-geometry columns hold exactly one feature — emit it as-is.
      if (!isMultiType) {
        onGeometryChangeRef.current(drawn[drawn.length - 1].geometry as GeoJSON.Geometry);
        return;
      }

      // Multi-geometry columns — combine same-typed features into a Multi* geometry.
      if (lineStrings.length > 0) {
        onGeometryChangeRef.current({
          type: "MultiLineString",
          coordinates: lineStrings.map((f: any) => (f.geometry as GeoJSON.LineString).coordinates)
        });
      } else if (polygons.length > 0) {
        onGeometryChangeRef.current({
          type: "MultiPolygon",
          coordinates: polygons.map((f: any) => (f.geometry as GeoJSON.Polygon).coordinates)
        });
      } else if (points.length > 0) {
        onGeometryChangeRef.current({
          type: "MultiPoint",
          coordinates: points.map((f: any) => (f.geometry as GeoJSON.Point).coordinates)
        });
      } else {
        onGeometryChangeRef.current(drawn[drawn.length - 1].geometry as GeoJSON.Geometry);
      }
    };

    const draw = new TerraDraw({
      adapter: new TerraDrawMapLibreGLAdapter({ map }),
      modes: [
        new TerraDrawPointMode({
          editable: true,
          styles: { pointColor: baseDrawColor, pointOutlineColor: "#fff", pointOutlineWidth: 2, pointWidth: 7 }
        }),
        new TerraDrawLineStringMode({
          editable: true,
          showCoordinatePoints: true,
          pointerDistance: 12,
          styles: {
            lineStringColor: baseDrawColor,
            lineStringWidth: 2,
            coordinatePointColor: baseDrawColor,
            coordinatePointOutlineColor: "#ffffff" as HexColor,
            coordinatePointOutlineWidth: 2,
            coordinatePointWidth: 5
          }
        }),
        new TerraDrawPolygonMode({
          editable: true,
          showCoordinatePoints: true,
          pointerDistance: 12,
          styles: {
            fillColor: baseDrawColor,
            fillOpacity: 0.25,
            outlineColor: baseDrawColor,
            outlineWidth: 2,
            coordinatePointColor: baseDrawColor,
            coordinatePointOutlineColor: "#ffffff" as HexColor,
            coordinatePointOutlineWidth: 2,
            coordinatePointWidth: 5
          }
        }),
        new TerraDrawRectangleMode({
          styles: { fillColor: baseDrawColor, fillOpacity: 0.25, outlineColor: baseDrawColor, outlineWidth: 2 }
        }),
        new TerraDrawRenderMode({ modeName: RENDER_MODE, styles: {} })
      ],
      undoRedo: {
        modeLevel: new TerraDrawModeUndoRedo(),
        keyboardShortcuts: new TerraDrawUndoRedoKeyboardShortcuts()
      }
    });

    // The `finish` event fires after every completed interaction — both drawing a new
    // feature (action "draw") and editing an existing one (dragCoordinate, insertMidpoint,
    // deleteCoordinate, dragFeature). Because modes are `editable`, all features stay
    // editable in place with no select/deselect step.
    draw.on("finish", (id, context) => {
      const feature = draw.getSnapshotFeature(id);

      if (context.action === "draw") {
        // Single-geometry columns hold one feature — drop any earlier ones.
        // Exclude internal terra-draw markers — terra-draw owns their lifecycle;
        // deleting them here causes "can not delete" crashes mid-transaction.
        if (!isMultiType) {
          const others = draw.getSnapshot().filter((f: any) => f.id !== id && f.properties.mode !== RENDER_MODE && !isInternalMarker(f));
          if (others.length > 0) draw.removeFeatures(others.map((f: any) => f.id as string));
        }
        // Rectangles are drawn in their own mode — re-tag as polygon so their
        // vertices remain editable in the always-on polygon mode.
        if (feature && feature.properties.mode === "rectangle") {
          draw.removeFeatures([id]);
          draw.addFeatures([toFeature(feature.geometry as GeoJSON.Geometry, "polygon")]);
        }
        // Return to the primary editable mode so the new feature is immediately editable.
        setActiveMode(null);
      }

      skipSyncRef.current = true;
      emitCombined(draw);

      // Keep the preview-icon anchor in sync with the drawn point (point tables only).
      if (baseSpatial === "point") {
        const pts = draw
          .getSnapshot()
          .filter((f: any) => f.geometry.type === "Point" && f.properties.mode !== RENDER_MODE && !isInternalMarker(f));
        setPointCoords(pts.length ? ((pts[pts.length - 1].geometry as GeoJSON.Point).coordinates as [number, number]) : null);
      }
    });

    draw.start();

    // The kind seeded from an existing row (edit mode) — anchors the primary editable
    // mode for the `geometry` catch-all so its features stay editable in place.
    let initialKind: "point" | "linestring" | "polygon" | null = null;

    if (externalGeometry) {
      const allComponents = expandToComponents(externalGeometry);
      // For single-geometry columns load only the first component so we never
      // seed the canvas with more than one feature (e.g. a saved FeatureCollection).
      const components = isMultiType ? allComponents : allComponents.slice(0, 1);
      applyVertexHandles(draw, components);
      // Add each component separately so a single invalid feature can't abort the
      // whole batch (leaving only the earlier features on the map).
      const added = components.filter(({ geometry, modeName }) => addComponent(draw, geometry, modeName, "init"));
      if (added.length > 0) {
        setHasGeometry(true);
        setDrawnKind(kindOfGeometry(added[0].geometry));
        initialKind = kindOfGeometry(added[0].geometry);
      }
      // Seed the preview-icon anchor from the initial point geometry (edit mode).
      if (baseSpatial === "point") {
        const firstPoint = added.find((c) => c.geometry.type === "Point");
        if (firstPoint) setPointCoords((firstPoint.geometry as GeoJSON.Point).coordinates as [number, number]);
      }
    }

    // Start in the primary editable mode so all features are immediately editable.
    // For the `geometry` catch-all this is the kind loaded from the existing row.
    draw.setMode(primaryModeFor(spatialType, initialKind));
    drawRef.current = draw;

    return () => {
      draw.stop();
      drawRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady]);

  // ── Drive Terra Draw's active mode from our toolbar state ──────────────────
  // Idle (no active draw tool) → the primary editable drawing mode for the spatial
  // type, so every feature's vertices are always visible and draggable. Picking a
  // specific tool (e.g. rectangle) temporarily switches to that mode.
  useEffect(() => {
    const draw = drawRef.current;
    if (!draw) return;
    if (activeMode) {
      draw.setMode(TERRA_DRAW_MODE[activeMode]);
      return;
    }
    draw.setMode(primaryModeFor(spatialType, drawnKind));
  }, [activeMode, spatialType, drawnKind]);

  // ── Live-recolor the drawn feature to preview the selected styling `type` ──
  // Point tables preview via an icon marker (see render); polygon/line tables
  // recolor Terra Draw's fill/outline/line without re-initialising the instance.
  useEffect(() => {
    const draw = drawRef.current;
    if (!draw || !previewColor) return;
    const b = normaliseSpatialType(spatialType);
    try {
      if (b === "polygon") {
        const styles = {
          fillColor: previewColor as HexColor,
          fillOpacity: 0.35,
          outlineColor: previewColor as HexColor,
          outlineWidth: 2,
          coordinatePointColor: previewColor as HexColor,
          coordinatePointOutlineColor: "#ffffff" as HexColor,
          coordinatePointOutlineWidth: 2,
          coordinatePointWidth: 5
        };
        (draw as any).updateModeOptions("polygon", { styles });
        (draw as any).updateModeOptions("rectangle", {
          styles: { fillColor: previewColor as HexColor, fillOpacity: 0.35, outlineColor: previewColor as HexColor, outlineWidth: 2 }
        });
      } else if (b === "linestring") {
        (draw as any).updateModeOptions("linestring", {
          styles: {
            lineStringColor: previewColor as HexColor,
            lineStringWidth: 2,
            coordinatePointColor: previewColor as HexColor,
            coordinatePointOutlineColor: "#ffffff" as HexColor,
            coordinatePointOutlineWidth: 2,
            coordinatePointWidth: 5
          }
        });
      }
    } catch (e) {
      console.warn("[CmTableDrawMap] preview color update failed:", e);
    }
  }, [previewColor, spatialType, mapReady, hasGeometry]);

  // ── Sync external geometry to map (Advanced GeoJSON Editor) ────────────────
  useEffect(() => {
    // Skip this cycle if the geometry change originated from our own drawing/editing —
    // prevents a feedback loop where emitting combined geometry re-triggers a clear.
    if (skipSyncRef.current) {
      skipSyncRef.current = false;
      return;
    }
    const draw = drawRef.current;
    if (!draw || !externalGeometry) return;

    setActiveMode(null);
    draw.clear();

    const allComponents = expandToComponents(externalGeometry);
    // For single-geometry columns limit to one component so pasting a
    // FeatureCollection into the Advanced GeoJSON Editor doesn't accumulate.
    const isMulti = isMultiSpatialType(spatialType);
    const components = isMulti ? allComponents : allComponents.slice(0, 1);
    let syncedKind: "point" | "linestring" | "polygon" | null = null;
    applyVertexHandles(draw, components);
    // Add each component separately so a single invalid feature can't abort the
    // whole batch (leaving only the earlier features on the map).
    const added = components.filter(({ geometry, modeName }) => addComponent(draw, geometry, modeName, "sync"));
    if (added.length > 0) {
      setHasGeometry(true);
      syncedKind = kindOfGeometry(added[0].geometry);
      setDrawnKind(syncedKind);
    }
    // Keep the preview-icon anchor aligned with the synced point geometry.
    if (normaliseSpatialType(spatialType) === "point") {
      const firstPoint = added.find((c) => c.geometry.type === "Point");
      setPointCoords(firstPoint ? ((firstPoint.geometry as GeoJSON.Point).coordinates as [number, number]) : null);
    }
    // Return to the primary editable mode so synced features stay editable in place.
    // For the `geometry` catch-all this is the synced kind.
    draw.setMode(primaryModeFor(spatialType, syncedKind));

    // Re-fit the viewport to the pasted/edited geometry so every component is
    // visible. This effect is skipped for draw-originated changes (skipSyncRef),
    // so it only fires on manual Advanced-GeoJSON edits — where the one-shot
    // auto-zoom has usually already run and won't move to the new geometry.
    if (added.length > 0) {
      const map = mapRef.current?.getMap();
      if (map) {
        try {
          const [minLng, minLat, maxLng, maxLat] = bbox(externalGeometry as any);
          if (isFinite(minLng)) {
            if (minLng === maxLng && minLat === maxLat) {
              map.flyTo({ center: [minLng, minLat], zoom: 14, duration: 500 });
            } else {
              map.fitBounds(
                [
                  [minLng, minLat],
                  [maxLng, maxLat]
                ],
                { padding: 60, maxZoom: 15, duration: 500 }
              );
            }
          }
        } catch (_) {}
      }
    }
    // Don't call onGeometryChange here — it came FROM the parent already
  }, [externalGeometry, spatialType]);

  // ── Auto-zoom to initial geometry once the map is ready (edit mode) ────────
  useEffect(() => {
    if (!mapReady || !externalGeometry || autoZoomedRef.current) return;
    autoZoomedRef.current = true;
    const map = mapRef.current?.getMap();
    if (!map) return;
    try {
      if (externalGeometry.type === "Point") {
        const [lng, lat] = (externalGeometry as GeoJSON.Point).coordinates;
        map.flyTo({ center: [lng, lat], zoom: 14, duration: 500 });
      } else {
        const [minLng, minLat, maxLng, maxLat] = bbox(externalGeometry);
        if (isFinite(minLng)) {
          map.fitBounds(
            [
              [minLng, minLat],
              [maxLng, maxLat]
            ],
            { padding: 60, maxZoom: 15, duration: 500 }
          );
        }
      }
    } catch (_) {}
  }, [mapReady, externalGeometry]);

  // ── Toolbar handlers ────────────────────────────────────────────────────────
  const handleModeChange = (mode: DrawMode) => {
    // Toggle: clicking the active tool deactivates it; clicking a different tool switches.
    // Features are NOT cleared on mode switch — use Reset to start over.
    setActiveMode((prev) => (prev === mode ? null : mode));
  };

  const handleReset = () => {
    setActiveMode(null);
    setHasGeometry(false);
    setDrawnKind(null);
    setPointCoords(null);
    drawRef.current?.clear();
    onGeometryChange(null);
  };

  const baseSpatialType = normaliseSpatialType(spatialType);
  const isMultiType = isMultiSpatialType(spatialType);
  // The `geometry` catch-all (base "none") lets the user pick any single kind and add
  // more of that kind — show a generic hint until a tool is chosen.
  const isGeometryCatchAll = baseSpatialType === "none" && (allowedTypes?.length ?? 0) > 0;
  const hintText = isGeometryCatchAll
    ? "Pick a tool to draw a point, line, or shape · Add more of the same kind · Drag points to edit"
    : baseSpatialType === "point"
      ? "Click the map to place a point · Drag it to reposition"
      : baseSpatialType === "linestring"
        ? isMultiType
          ? "Click to add points · Double-click to finish · Draw again to add more lines · Drag points to edit · Click a line to add a point"
          : "Click to add points · Double-click to finish · Drag points to edit · Click the line to add a point"
        : isMultiType
          ? "Click to add points · Double-click to close · Draw again to add more shapes · Drag points to edit · Click an edge to add a point"
          : "Click to add points · Double-click to close · Drag points to edit · Click an edge to add a point";

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%", cursor: cursorStyle }}>
      {/* Compact hint bar */}
      {!hasGeometry && !externalGeometry && (
        <Box
          sx={{
            position: "absolute",
            top: "0.5rem",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10,
            bgcolor: "rgba(0,0,0,0.6)",
            color: "white",
            borderRadius: "0.5rem",
            px: "0.75rem",
            py: "0.3rem",
            pointerEvents: "none",
            whiteSpace: "nowrap"
          }}
        >
          <Typography variant='caption' sx={{ fontSize: "0.72rem" }}>
            {hintText}
          </Typography>
        </Box>
      )}

      <Map
        ref={mapRef}
        style={{ width: "100%", height: "100%" }}
        initialViewState={initialViewState ?? EMPTY_VIEW_STATE}
        mapStyle={APP_BASEMAP_STYLE}
        attributionControl={false}
        onLoad={() => setMapReady(true)}
      >
        {userLocation && (
          <Marker longitude={userLocation.longitude} latitude={userLocation.latitude} anchor='center'>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                backgroundColor: "#00bfff",
                border: "2px solid white",
                boxShadow: "0 2px 8px rgba(0,191,255,0.4)",
                animation: "bluePulse 2s ease-in-out infinite",
                "@keyframes bluePulse": {
                  "0%": { transform: "scale(1)", opacity: 1 },
                  "50%": { transform: "scale(1.1)", opacity: 0.8 },
                  "100%": { transform: "scale(1)", opacity: 1 }
                }
              }}
            />
          </Marker>
        )}

        {/* Preview icon for the selected styling `type` (point tables) */}
        {previewIcon && baseSpatialType === "point" && pointCoords && (
          <Marker longitude={pointCoords[0]} latitude={pointCoords[1]} anchor='bottom'>
            <Box
              component='img'
              src={previewIcon}
              alt=''
              sx={{ width: 34, height: 34, objectFit: "contain", pointerEvents: "none", filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.4))" }}
            />
          </Marker>
        )}
      </Map>

      {/* Drawing toolbar — bottom center */}
      <Box
        sx={{
          position: "absolute",
          bottom: "0.75rem",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 10
        }}
      >
        <DrawingTools
          spatialType={spatialType}
          allowedTypes={allowedTypes}
          activeMode={activeMode}
          onModeChange={handleModeChange}
          onReset={handleReset}
          lockedKind={(allowedTypes?.length ?? 0) > 1 ? drawnKind : null}
        />
      </Box>

      {/* Navigation controls — bottom right */}
      <Box sx={{ position: "absolute", bottom: "0.75rem", right: "0.75rem", zIndex: 10 }}>
        <MapControls
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onLocationRequest={handleLocationRequest}
          isTrackingLocation={isTrackingLocation}
          isGettingLocation={isGettingLocation}
        />
      </Box>
    </Box>
  );
};

export default CmTableDrawMap;
